import { useQuery, useQueryClient } from '@tanstack/react-query';

import { authService } from '@/shared/api';
import type { User } from '@/shared/types/auth';

/**
 * Centralized hook for user data with caching
 * This replaces direct calls to authService.getMe() and provides:
 * - Automatic caching with TTL
 * - Intelligent refetching
 * - Single source of truth for user data
 */
export const useUserData = (options?: {
  enabled?: boolean;
  staleTime?: number;
  refetchInterval?: number;
}) => {
  const {
    enabled = true,
    staleTime = 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
    refetchInterval = 15 * 60 * 1000, // 15 minutes - background refetch
  } = options || {};

  return useQuery({
    queryKey: ['user', 'me'],
    queryFn: async (): Promise<User> => {
      const response = await authService.getMe(); // Use normal flow with tokens

      if (response.type !== 'success') {
        throw new Error('Failed to fetch user data');
      }

      if (!('id' in response.data)) {
        throw new Error('User not authenticated');
      }

      return response.data;
    },
    enabled,
    staleTime, // Data is considered fresh for this time
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache
    refetchInterval, // Background refetch every 15 minutes
    refetchIntervalInBackground: false, // Don't refetch when tab is not active
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch on component mount if data is fresh
    retry: (failureCount, error) => {
      // Only retry on network errors, not auth errors
      if (error instanceof Error && error.message.includes('not authenticated')) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

/**
 * Hook to invalidate user data cache
 * Use this when user data changes (login, logout, profile updates)
 */
export const useInvalidateUserData = () => {
  const queryClient = useQueryClient();

  return {
    invalidateUserData: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'me'] });
    },
    removeUserData: () => {
      queryClient.removeQueries({ queryKey: ['user', 'me'] });
    },
    setUserData: (userData: User) => {
      queryClient.setQueryData(['user', 'me'], userData);
    },
  };
};

/**
 * Hook to get cached user data without triggering requests
 * Useful for components that need user data but don't want to cause loading states
 */
export const useCachedUserData = (): User | undefined => {
  const queryClient = useQueryClient();
  return queryClient.getQueryData(['user', 'me']);
};
